import { FC, useContext, useRef, useState } from "react";
import { Button } from "primereact/button";
import { MobXProviderContext } from "mobx-react";
import { Controller, useForm } from "react-hook-form";
import { classNames } from "primereact/utils";
import { GROUP_NAME_AI_COMPANION_ADMIN, ROLE_TYPE_PROVIDER } from "../../constants/RegisterConstants";
import { Toast } from "primereact/toast";
import { RadioButton } from "primereact/radiobutton";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { APP_DOCUMENT_FILE_SIZE_IN_BYTES, getErrorTitle } from "../../constants/constants";
import { TabPanel, TabView } from "primereact/tabview";

const SearchWithAIModal = () => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const userRoleType = authStore.getUserRoleType();
  const userGroups = authStore.getUserGroups();
  const canUploadDocuments = userRoleType === ROLE_TYPE_PROVIDER && userGroups.includes(GROUP_NAME_AI_COMPANION_ADMIN);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<any>({ systemMessage: '', sources: [] });
  const [loadingMessage, setLoadingMessage] = useState<string>('');


  const SEARCH_TYPES = [
    { name: "DSHS Content", value: "DSHS Content" },
    { name: "Hub Content", value: "Hub Content" },
  ]

  const defaultValues = {
    question: '',
    searchSource: '',
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onSearchSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);
    setLoadingMessage('Searching AI Companion ... Query will take 5-8 seconds.');
    try {
      await backendAPIStore.init();
      const searchResponse = await backendAPIStore.searchAICompanion(data.question, data.searchSource);
      setSearchResults(searchResponse.body);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
      reset();
    }
  };

  const handleFilesUpload = async (
    event: any,
  ) => {
    if (event.files && event.files.length > 0) {
      const files = Array.from(event.files);
      if (files.length > 0) {
        await Promise.all(files.map((file) => processDocument(file)));
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
          toast.current?.show({ severity: 'success', summary: 'Success', detail: 'All files uploaded successfully. ', life: 3000 });
        }
      }
    }
  };

  const processDocument = async (file: File | any) => {
    try {
      await backendAPIStore.init();
      const uploadUrl = await backendAPIStore.getPresignedUrlForAICompanionFileUpload(file.name);
      await backendAPIStore.uploadFileToS3(uploadUrl, file);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      toast.current?.show({ severity: 'info', summary: 'Info', detail: `File ${file.name} uploaded.`, life: 1000 });
    }
  };

  const sourceRowTemplate = (rowData) => {
    return (
      <div>
        <a href={rowData.url} target="_blank" rel="noopener noreferrer"><b>{rowData.title}</b></a>
        <div className="overflow-wrap">{rowData.url}</div>
        <br />
        <b>Snippet used from this content:</b>
        <p>{rowData.snippet}</p>
      </div>
    )
  }

  const titleTemplate = (title) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>{title}</span>
      </div>
    );
  };

  return (
    <div className="container-fluid m-1">
      <Toast ref={toast} />
      <div className="m-5 justify-content-center align-items-center ">
        <TabView>
          <TabPanel header="Search">
            <form className="grid flex-column md:flex-row gap-4">
              <div className="col">
                <div>
                  <span>
                    <Controller name="question" control={control} rules={{ required: 'Question is required.' }} render={({ field, fieldState }) => (
                      <InputTextarea id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} rows={5}
                        style={{ width: "100%" }} placeholder="Write your question here ..." />
                    )} />
                  </span>
                  {getFormErrorMessage('question')}
                </div>
                <div>
                  <h4>Search from:</h4>
                  <div className="p-field-radiobutton">
                    {SEARCH_TYPES.map((option) => (
                      <div key={option.value} className="p-field-radiobutton">
                        <Controller
                          name="searchSource"
                          control={control}
                          rules={{ required: 'Please select an option.' }}
                          render={({ field }) => (
                            <RadioButton
                              {...field}
                              inputId={option.value}
                              value={option.value}
                              checked={field.value === option.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                        <label htmlFor={option.value}>{option.name}</label>
                      </div>
                    ))}
                  </div>

                  {getFormErrorMessage('searchSource')}
                  <Button type="submit" label="Search" severity="warning" rounded className="mb-2 p-button-outlined" loading={loading} onClick={handleSubmit(onSearchSubmit)} />
                  <div style={{ color: "gray" }}>{loadingMessage}</div>
                </div>
              </div>

              <div className="col p-0">
                <div>
                  <div>Sample questions you can ask:</div>
                  <ul style={{ color: 'gray' }}>
                    <li>What are the licensing requirements for assisted living facilities in Washington State?</li>
                    <li>Can you provide information on the types of care services offered by assisted living facilities?</li>
                    <li>What are the regulations regarding staff-to-resident ratios in assisted living facilities?</li>
                    <li>How do assisted living facilities handle medication management for residents?</li>
                    <li>What are the safety and emergency protocols that assisted living facilities must follow?</li>
                  </ul>
                </div>
              </div>
            </form>
          </TabPanel>

          {canUploadDocuments ? (
            <TabPanel header="Add documents">
              <FileUpload ref={fileUploadRef} name="aiCompanionFiles" customUpload uploadHandler={handleFilesUpload} maxFileSize={APP_DOCUMENT_FILE_SIZE_IN_BYTES}
                emptyTemplate={<p className="m-0">Add documents to Hub Content</p>}
                multiple />
            </TabPanel>) : null}
        </TabView>

        {searchResults.systemMessage ? (
          <Card title={titleTemplate("AI Generated Answer")} className="mb-3">
            <p>{searchResults.systemMessage}</p>
          </Card>) : null}

        {searchResults.sources.length > 0 &&
          <div>
            <h4>Following {searchResults.sources.length} sources are used to generate the response:</h4>
            <br />
            {searchResults.sources.map((source, index) => (
              <Card key={index} className="mb-3">
                <div key={index} className="">
                  {sourceRowTemplate(source)}
                </div>
              </Card>))}
          </div>}
      </div >
    </div >
  );
}

export default SearchWithAIModal;
